import React from "react";
import { Card, PageHeader } from "@pericles/framework-frontend";

import { useAuth } from "../hooks/auth";

import { Input, PageShell } from "./ui";

type TenantSummary = {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
};

type InternalAdminPanelProps = {
  showTitle?: boolean;
};

type ParsedResponse<T> = {
  data: T | null;
  text: string;
};

async function parseResponse<T>(response: Response): Promise<ParsedResponse<T>> {
  const text = await response.text();
  if (!text) {
    return { data: null, text: "" };
  }
  try {
    return { data: JSON.parse(text) as T, text };
  } catch {
    return { data: null, text };
  }
}

export function InternalAdminPanel({ showTitle = false }: InternalAdminPanelProps): JSX.Element {
  const { user } = useAuth();
  const [tenants, setTenants] = React.useState<TenantSummary[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [form, setForm] = React.useState({ name: "", slug: "", ownerEmail: "" });
  const [tempPassword, setTempPassword] = React.useState<string | null>(null);
  const isPlatformAdmin = user?.isPlatformAdmin === true;

  const headers = React.useMemo(() => {
    return { accept: "application/json" } satisfies Record<string, string>;
  }, []);

  const fetchTenants = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch("/internal/admin/tenants", { headers, credentials: "same-origin" });
      const { data: payload, text } = await parseResponse<{ tenants?: TenantSummary[]; error?: string }>(response);
      if (!response.ok) {
        const message = payload?.error ?? text;
        throw new Error(message || "Unable to load tenants");
      }
      if (!payload) {
        throw new Error("Unexpected response while loading tenants.");
      }
      setTenants(payload.tenants ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load tenants");
    } finally {
      setIsLoading(false);
    }
  }, [headers]);

  React.useEffect(() => {
    if (!isPlatformAdmin) return;
    void fetchTenants();
  }, [fetchTenants, isPlatformAdmin]);

  React.useEffect(() => {
    if (isPlatformAdmin) return;
    setTenants([]);
  }, [isPlatformAdmin]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setTempPassword(null);
    try {
      const response = await fetch("/internal/admin/tenants", {
        method: "POST",
        headers: { ...headers, "content-type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(form),
      });
      const { data: payload, text } = await parseResponse<{ tempPassword?: string; error?: string }>(response);
      if (!response.ok) {
        const message = payload?.error ?? text;
        throw new Error(message || "Failed to create tenant");
      }
      if (!payload) {
        throw new Error("Unexpected response while creating tenant.");
      }
      if (payload.tempPassword) {
        setTempPassword(payload.tempPassword);
      }
      setForm({ name: "", slug: "", ownerEmail: "" });
      await fetchTenants();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create tenant");
    }
  }

  return (
    <>
      {showTitle ? (
        <>
          <h1 className="text-2xl font-semibold text-slate-900">Internal Admin</h1>
          <p className="text-sm text-slate-600">Signed in as {user?.email}</p>
        </>
      ) : (
        <p className="text-sm text-slate-600">Signed in as {user?.email}</p>
      )}

      <Card className="mt-4" bodyClassName="flex flex-col gap-2">
        <h2 className="text-lg font-semibold">Access</h2>
        <p className="text-sm text-slate-600">
          {isPlatformAdmin
            ? "Platform administrator session verified. Sensitive operations are audited server-side."
            : "A platform administrator session is required for tenant provisioning."}
        </p>
      </Card>

      <Card className="mt-6" bodyClassName="flex flex-col gap-3">
        <h2 className="text-lg font-semibold">Provision tenant</h2>
        <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
          <label className="flex flex-col gap-1 text-sm">
            Tenant name
            <Input
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              required
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Tenant slug
            <Input
              value={form.slug}
              onChange={(event) => setForm((prev) => ({ ...prev, slug: event.target.value }))}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Owner email
            <Input
              type="email"
              value={form.ownerEmail}
              onChange={(event) => setForm((prev) => ({ ...prev, ownerEmail: event.target.value }))}
              required
            />
          </label>
          <button className="button button-primary" type="submit" disabled={!isPlatformAdmin}>
            Create tenant + owner
          </button>
        </form>
        {tempPassword ? (
          <p className="text-sm text-slate-700">
            Temporary password: <span className="font-mono">{tempPassword}</span>
          </p>
        ) : null}
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
      </Card>

      <Card className="mt-6" bodyClassName="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Tenants</h2>
          <button
            className="button button-secondary button-sm"
            type="button"
            onClick={fetchTenants}
            disabled={!isPlatformAdmin}
          >
            Refresh
          </button>
        </div>
        {isLoading ? <p className="text-sm text-slate-600">Loading…</p> : null}
        <ul className="space-y-2">
          {tenants.map((tenant) => (
            <li key={tenant.id} className="rounded border border-slate-100 p-2">
              <div className="font-semibold">{tenant.name}</div>
              <div className="text-xs text-slate-600">
                {tenant.slug} · {tenant.id}
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}

export function InternalAdminScreen(): JSX.Element {
  return (
    <PageShell className="mx-auto max-w-3xl p-6">
      <PageHeader title="Internal Admin" subtitle="Provision tenants and owners for the studio workspace." />
      <InternalAdminPanel />
    </PageShell>
  );
}
